import jwtDecode from 'jwt-decode';
import { verify, sign } from 'jsonwebtoken';
import axios from './axios';
import { ACCESS_TOKEN_NAME, ACCESS_TOKEN_USER } from '../const';

/**
 * Sprint 1+2 role bridge.
 *
 * Backend now issues string roles (admin / salesperson / production / customer)
 * and includes user_type + customer_id in the JWT payload. Older tokens still
 * carry numeric role values; we accept both shapes via LEGACY_ROLE_MAP, which
 * mirrors addictive-backend/constants/roles.js.
 */

/**
 * IMPORTANT - Customer role frontend value:
 *   The pre-Sprint-1 frontend used the string 'client' for role=2 throughout
 *   pages (LeadManagement, SiteCard, RequestLeadButton, etc.). To avoid
 *   breaking those pages while also accepting the backend's new canonical
 *   string 'customer', ROLES.CUSTOMER is fixed to 'client' here and any
 *   incoming 'customer' / 2 / 'client' is normalized to 'client'.
 *   `getUserType()` independently returns 'customer' / 'internal' for the
 *   new dual-auth flow.
 */
export const ROLES = {
  ADMIN: 'admin',
  SALESPERSON: 'salesperson',
  PRODUCTION: 'production',
  CUSTOMER: 'client',
  PARTNER: 'partner',
  USER: 'user',
};

export const INTERNAL_ROLES = [ROLES.ADMIN, ROLES.SALESPERSON, ROLES.PRODUCTION];

const LEGACY_ROLE_MAP = {
  1: ROLES.ADMIN,
  2: ROLES.CUSTOMER,
  3: ROLES.SALESPERSON,
  4: ROLES.PRODUCTION,
  5: ROLES.SALESPERSON,
};

const STRING_ROLE_ALIASES = {
  client: ROLES.CUSTOMER,
  customer: ROLES.CUSTOMER,
  admin: ROLES.ADMIN,
  salesperson: ROLES.SALESPERSON,
  production: ROLES.PRODUCTION,
  partner: ROLES.PARTNER,
  user: ROLES.USER,
};

export const normalizeRole = (raw) => {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'number') {
    return LEGACY_ROLE_MAP[raw] || null;
  }
  const key = String(raw).toLowerCase();
  return STRING_ROLE_ALIASES[key] || null;
};

export const isInternalRole = (role) => INTERNAL_ROLES.includes(normalizeRole(role));

const getActiveToken = () =>
  window.localStorage.getItem(ACCESS_TOKEN_USER) ||
  window.localStorage.getItem(ACCESS_TOKEN_NAME);

const isValidToken = (accessToken) => {
  if (!accessToken) return false;
  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now();

  return decoded.iat < currentTime;
};

const isUserRole = () => {
  const accessToken = getActiveToken();
  if (!accessToken) return null;
  const decoded = jwtDecode(accessToken);
  const fromString = normalizeRole(decoded.role);
  if (fromString) return fromString;
  // Sprint 6 cutover: only canonical string roles are accepted. The legacy
  // numeric 5 -> partner mapping is gone; any unknown value falls through
  // to ROLES.USER to keep older tokens from crashing the sidebar.
  return ROLES.USER;
};

const getJwtPayload = () => {
  const accessToken = getActiveToken();
  if (!accessToken) return null;
  try {
    return jwtDecode(accessToken);
  } catch (err) {
    return null;
  }
};

const getUserType = () => {
  const payload = getJwtPayload();
  if (!payload) return null;
  if (payload.user_type) return payload.user_type;
  const role = normalizeRole(payload.role);
  if (role === ROLES.CUSTOMER) return 'customer';
  if (INTERNAL_ROLES.includes(role)) return 'internal';
  return null;
};

const getCustomerId = () => {
  const payload = getJwtPayload();
  return payload && payload.customer_id ? payload.customer_id : null;
};

const isUser = () => {
  const accessToken = getActiveToken();
  if (!accessToken) return null;
  return jwtDecode(accessToken);
};

const setSession = (tokenName, accessToken) => {
  if (accessToken) {
    window.localStorage.setItem(tokenName, accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    window.localStorage.removeItem(tokenName);
    delete axios.defaults.headers.common.Authorization;
  }
};

export {
  verify,
  sign,
  isValidToken,
  setSession,
  isUserRole,
  isUser,
  getJwtPayload,
  getUserType,
  getCustomerId,
};
